import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  composeOperaCredentialsRequest,
  createOperaCredentialsSchema,
  OperaCredentialsFormData,
} from "../../utils/pmsSchemas";
import {
  CredentialsStatus,
  OperaGrantType,
  OperaTransport,
  PmsSettings,
  UpdateOperaCredentialsRequest,
} from "@/types/pms";

const GRANT_TYPE_OPTIONS: { value: OperaGrantType; labelKey: string }[] = [
  {
    value: "client_credentials",
    labelKey: "pms.credentials.opera.grantTypeOptions.client_credentials",
  },
  {
    value: "password",
    labelKey: "pms.credentials.opera.grantTypeOptions.password",
  },
];

const TRANSPORT_OPTIONS: { value: OperaTransport; labelKey: string }[] = [
  {
    value: "polling",
    labelKey: "pms.credentials.opera.transportOptions.polling",
  },
  {
    value: "streaming",
    labelKey: "pms.credentials.opera.transportOptions.streaming",
  },
];

interface OperaCredentialsFormProps {
  settings: PmsSettings | null;
  credentials: CredentialsStatus | null;
  canWrite: boolean;
  isSaving: boolean;
  onSave: (data: UpdateOperaCredentialsRequest) => void;
  missingFields?: (keyof OperaCredentialsFormData)[];
}

export const OperaCredentialsForm = ({
  settings,
  credentials,
  canWrite,
  isSaving,
  onSave,
  missingFields,
}: OperaCredentialsFormProps) => {
  const { t } = useTranslation();

  // baseUrl and pmsHotelId are the only credential fields the API returns;
  // every other field renders empty by design (secrets are never echoed back).
  const prefillBaseUrl = credentials?.baseUrl ?? settings?.baseUrl ?? "";
  const prefillHotelId = credentials?.pmsHotelId ?? settings?.pmsHotelId ?? "";
  const hasAdvancedConfig = credentials?.hasAdvancedConfig ?? false;

  const emptyValues: OperaCredentialsFormData = React.useMemo(
    () => ({
      baseUrl: prefillBaseUrl,
      pmsHotelId: prefillHotelId,
      clientToken: "",
      grantType: "client_credentials",
      appKey: "",
      clientId: "",
      enterpriseId: "",
      scope: "",
      username: "",
      password: "",
      extSystemCode: "",
      transport: "polling",
      chainCode: "",
      wssUrl: "",
      cashierId: "",
    }),
    [prefillBaseUrl, prefillHotelId],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<OperaCredentialsFormData>({
    resolver: zodResolver(createOperaCredentialsSchema()),
    defaultValues: emptyValues,
  });

  React.useEffect(() => {
    reset(emptyValues);
  }, [emptyValues, reset]);

  // Errors set from a failed test-connection survive until the next resolver
  // pass (mode is onSubmit), so clear the previous batch whenever a new test
  // result arrives — including a successful one, which sends an empty list.
  const appliedMissingFields = React.useRef<(keyof OperaCredentialsFormData)[]>(
    [],
  );

  React.useEffect(() => {
    if (appliedMissingFields.current.length) {
      clearErrors(appliedMissingFields.current);
      appliedMissingFields.current = [];
    }
    if (!missingFields?.length) return;
    missingFields.forEach((field) => {
      setError(field, {
        type: "manual",
        message: t("pms.credentials.opera.missingFromTest"),
      });
    });
    appliedMissingFields.current = missingFields;
  }, [missingFields, clearErrors, setError, t]);

  const grantType = watch("grantType");
  const transport = watch("transport");
  const isClientCredentials = grantType === "client_credentials";
  const isStreaming = transport === "streaming";

  const submit = (data: OperaCredentialsFormData) => {
    onSave(composeOperaCredentialsRequest(data));
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
      <h4 className="text-sm font-medium">
        {t("pms.credentials.opera.connectionGroup")}
      </h4>

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-baseUrl">
          {t("pms.credentials.opera.gatewayUrl")}
        </Label>
        <Input
          id="opera-baseUrl"
          placeholder={t("pms.credentials.opera.gatewayUrlPlaceholder")}
          disabled={!canWrite}
          {...register("baseUrl")}
        />
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.gatewayUrlHelp")}
        </p>
        {errors.baseUrl && (
          <p className="text-destructive text-sm">{errors.baseUrl.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-pmsHotelId">
          {t("pms.credentials.opera.propertyCode")}
        </Label>
        <Input
          id="opera-pmsHotelId"
          placeholder={t("pms.credentials.opera.propertyCodePlaceholder")}
          disabled={!canWrite}
          {...register("pmsHotelId")}
        />
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.propertyCodeHelp")}
        </p>
        {errors.pmsHotelId && (
          <p className="text-destructive text-sm">
            {errors.pmsHotelId.message}
          </p>
        )}
      </div>

      {isClientCredentials && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-clientToken">
            {t("pms.credentials.opera.clientSecret")}
          </Label>
          <Input
            id="opera-clientToken"
            type="password"
            placeholder={t("pms.credentials.opera.clientSecretPlaceholder")}
            disabled={!canWrite}
            {...register("clientToken")}
          />
          <p className="text-muted-foreground text-sm">
            {t("pms.credentials.opera.clientSecretHelp")}
          </p>
          {errors.clientToken && (
            <p className="text-destructive text-sm">
              {errors.clientToken.message}
            </p>
          )}
        </div>
      )}

      <Separator />

      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium">
          {t("pms.credentials.opera.ohipGroup")}
        </h4>
        {hasAdvancedConfig && (
          <Badge variant="filled_status_completed">
            {t("pms.credentials.opera.configStored")}
          </Badge>
        )}
      </div>
      {hasAdvancedConfig && (
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.reenterHint")}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-grantType">
          {t("pms.credentials.opera.grantType")}
        </Label>
        <Select
          value={grantType}
          onValueChange={(value) =>
            setValue("grantType", value as OperaGrantType, {
              shouldValidate: true,
            })
          }
          disabled={!canWrite}
        >
          <SelectTrigger id="opera-grantType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRANT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.grantTypeHelp")}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-appKey">
          {t("pms.credentials.opera.appKey")}
        </Label>
        <Input
          id="opera-appKey"
          type="password"
          placeholder={t("pms.credentials.opera.appKeyPlaceholder")}
          disabled={!canWrite}
          {...register("appKey")}
        />
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.appKeyHelp")}
        </p>
        {errors.appKey && (
          <p className="text-destructive text-sm">{errors.appKey.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-clientId">
          {t("pms.credentials.opera.clientId")}
        </Label>
        <Input
          id="opera-clientId"
          placeholder={t("pms.credentials.opera.clientIdPlaceholder")}
          disabled={!canWrite}
          {...register("clientId")}
        />
        {errors.clientId && (
          <p className="text-destructive text-sm">{errors.clientId.message}</p>
        )}
      </div>

      {isClientCredentials && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-enterpriseId">
            {t("pms.credentials.opera.enterpriseId")}
          </Label>
          <Input
            id="opera-enterpriseId"
            placeholder={t("pms.credentials.opera.enterpriseIdPlaceholder")}
            disabled={!canWrite}
            {...register("enterpriseId")}
          />
          {errors.enterpriseId && (
            <p className="text-destructive text-sm">
              {errors.enterpriseId.message}
            </p>
          )}
        </div>
      )}

      {isClientCredentials && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-scope">
            {t("pms.credentials.opera.scope")}
          </Label>
          <Input
            id="opera-scope"
            placeholder={t("pms.credentials.opera.scopePlaceholder")}
            disabled={!canWrite}
            {...register("scope")}
          />
          <p className="text-muted-foreground text-sm">
            {t("pms.credentials.opera.scopeHelp")}
          </p>
        </div>
      )}

      {!isClientCredentials && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-username">
            {t("pms.credentials.opera.username")}
          </Label>
          <Input
            id="opera-username"
            disabled={!canWrite}
            {...register("username")}
          />
          <p className="text-muted-foreground text-sm">
            {t("pms.credentials.opera.usernameHelp")}
          </p>
          {errors.username && (
            <p className="text-destructive text-sm">
              {errors.username.message}
            </p>
          )}
        </div>
      )}

      {!isClientCredentials && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-password">
            {t("pms.credentials.opera.password")}
          </Label>
          <Input
            id="opera-password"
            type="password"
            disabled={!canWrite}
            {...register("password")}
          />
          {errors.password && (
            <p className="text-destructive text-sm">
              {errors.password.message}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-extSystemCode">
          {t("pms.credentials.opera.extSystemCode")}
        </Label>
        <Input
          id="opera-extSystemCode"
          disabled={!canWrite}
          {...register("extSystemCode")}
        />
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.extSystemCodeHelp")}
        </p>
        {errors.extSystemCode && (
          <p className="text-destructive text-sm">
            {errors.extSystemCode.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-transport">
          {t("pms.credentials.opera.transport")}
        </Label>
        <Select
          value={transport}
          onValueChange={(value) =>
            setValue("transport", value as OperaTransport, {
              shouldValidate: true,
            })
          }
          disabled={!canWrite}
        >
          <SelectTrigger id="opera-transport">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSPORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.transportHelp")}
        </p>
      </div>

      {isStreaming && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-chainCode">
            {t("pms.credentials.opera.chainCode")}
          </Label>
          <Input
            id="opera-chainCode"
            placeholder={t("pms.credentials.opera.chainCodePlaceholder")}
            disabled={!canWrite}
            {...register("chainCode")}
          />
          {errors.chainCode && (
            <p className="text-destructive text-sm">
              {errors.chainCode.message}
            </p>
          )}
        </div>
      )}

      {isStreaming && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="opera-wssUrl">
            {t("pms.credentials.opera.wssUrl")}
          </Label>
          <Input
            id="opera-wssUrl"
            disabled={!canWrite}
            {...register("wssUrl")}
          />
          <p className="text-muted-foreground text-sm">
            {t("pms.credentials.opera.wssUrlHelp")}
          </p>
          {errors.wssUrl && (
            <p className="text-destructive text-sm">{errors.wssUrl.message}</p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="opera-cashierId">
          {t("pms.credentials.opera.cashierId")}
        </Label>
        <Input
          id="opera-cashierId"
          type="number"
          inputMode="numeric"
          disabled={!canWrite}
          {...register("cashierId")}
        />
        <p className="text-muted-foreground text-sm">
          {t("pms.credentials.opera.cashierIdHelp")}
        </p>
        {errors.cashierId && (
          <p className="text-destructive text-sm">{errors.cashierId.message}</p>
        )}
      </div>

      <Button
        type="submit"
        variant="new_default"
        disabled={!canWrite || isSaving}
        className="self-start"
      >
        {isSaving ? t("pms.credentials.saving") : t("pms.credentials.save")}
      </Button>
    </form>
  );
};
